import React, { useState, useEffect } from 'react';
import { Input, Button, Alert } from '../common';
import { User, Mail, Lock, Building2, Shield } from 'lucide-react';

const UserForm = ({
  user = null,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
}) => {
  const isEdit = !!user;

  const [formData, setFormData] = useState({
    nombre_completo: '',
    email: '',
    password: '',
    confirm_password: '',
    rol: 'miembro',
    id_organizacion: '',
    activo: true,
  });

  const [formErrors, setFormErrors] = useState({});

  // Cargar datos del usuario si es edición
  useEffect(() => {
    if (user) {
      setFormData({
        nombre_completo: user.nombre_completo || '',
        email: user.email || '',
        password: '',
        confirm_password: '',
        rol: user.rol || 'miembro',
        id_organizacion: user.id_organizacion || '',
        activo: user.activo !== false,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    // Limpiar error del campo
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.nombre_completo.trim()) {
      errors.nombre_completo = 'El nombre completo es requerido';
    }

    if (!formData.email.trim()) {
      errors.email = 'El email es requerido';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Email inválido';
      }
    }

    // Validar password solo si es creación o si se está cambiando
    if (!isEdit || formData.password) {
      if (!isEdit && !formData.password) {
        errors.password = 'La contraseña es requerida';
      } else if (formData.password && formData.password.length < 8) {
        errors.password = 'La contraseña debe tener al menos 8 caracteres';
      }

      if (formData.password !== formData.confirm_password) {
        errors.confirm_password = 'Las contraseñas no coinciden';
      }
    }

    if (!formData.rol) {
      errors.rol = 'El rol es requerido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Preparar datos
    const submitData = {
      nombre_completo: formData.nombre_completo,
      email: formData.email,
      rol: formData.rol,
      activo: formData.activo,
    };

    // Agregar password solo si se proporcionó
    if (formData.password) {
      submitData.password = formData.password;
    }

    // Agregar organización si se proporcionó
    if (formData.id_organizacion) {
      submitData.id_organizacion = parseInt(formData.id_organizacion);
    }

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert type="error" message={error} />
      )}

      <Input
        label="Nombre completo"
        name="nombre_completo"
        type="text"
        placeholder="Juan Pérez"
        value={formData.nombre_completo}
        onChange={handleChange}
        error={formErrors.nombre_completo}
        icon={User}
        required
        disabled={loading}
      />

      <Input
        label="Correo electrónico"
        name="email"
        type="email"
        placeholder="juan@ejemplo.com"
        value={formData.email}
        onChange={handleChange}
        error={formErrors.email}
        icon={Mail}
        required
        disabled={loading}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={isEdit ? 'Nueva contraseña (opcional)' : 'Contraseña'}
          name="password"
          type="password"
          placeholder={isEdit ? 'Dejar en blanco para mantener' : 'Mínimo 8 caracteres'}
          value={formData.password}
          onChange={handleChange}
          error={formErrors.password}
          icon={Lock}
          required={!isEdit}
          disabled={loading}
        />

        <Input
          label="Confirmar contraseña"
          name="confirm_password"
          type="password"
          placeholder="Repite la contraseña"
          value={formData.confirm_password}
          onChange={handleChange}
          error={formErrors.confirm_password}
          icon={Lock}
          required={!isEdit || !!formData.password}
          disabled={loading}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rol <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Shield className="h-5 w-5 text-gray-400" />
            </div>
            <select
              name="rol"
              value={formData.rol}
              onChange={handleChange}
              disabled={loading}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="miembro">Miembro</option>
              <option value="admin_org">Administrador de Organización</option>
              <option value="superadmin">Super Administrador</option>
            </select>
          </div>
          {formErrors.rol && (
            <p className="mt-1 text-sm text-red-600">{formErrors.rol}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Organización
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Building2 className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="number"
              name="id_organizacion"
              value={formData.id_organizacion}
              onChange={handleChange}
              placeholder="ID de organización"
              disabled={loading}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Opcional. Dejar en blanco si no aplica.
          </p>
        </div>
      </div>

      <div className="flex items-center">
        <input
          id="activo"
          name="activo"
          type="checkbox"
          checked={formData.activo}
          onChange={handleChange}
          disabled={loading}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="activo" className="ml-2 block text-sm text-gray-900">
          Usuario activo
        </label>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
        >
          {isEdit ? 'Actualizar Usuario' : 'Crear Usuario'}
        </Button>
      </div>
    </form>
  );
};

export default UserForm;
